
import { useEffect } from 'react';

import { REMOVE_PASS_DESCRIPTION_1, REMOVE_PASS_DESCRIPTION_2 } from './removePassModalConstants';

interface RemovePassModalProps {
  open: boolean;
  onKeep: () => void;
  onRemove: () => void;
}

const RemovePassModal = ({ open, onKeep, onRemove }: RemovePassModalProps) => {
  // Prevent background scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[7px]" />

      {/* Modal */}
      <div
        className="relative z-10 flex flex-col"
        style={getModalStyle()}
      >
        {/* Close icon */}
        <button
          onClick={onKeep}
          aria-label="Close"
          className="absolute top-5 right-5 flex items-center justify-center text-[#D0D5DD] hover:text-[#667085] bg-transparent border-none"
          style={{
            width: 20,
            height: 20,
            minWidth: 20,
            minHeight: 20,
            maxWidth: 20,
            maxHeight: 20,
            padding: 0,
            borderRadius: 4,
            fontSize: 20,
            lineHeight: 1,
            gap: 4,
            opacity: 1,
            background: 'transparent',
            boxShadow: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        {/* ========== FIRST COMPARTMENT ========== */}
        <div
          className="flex flex-col"
          style={getCompartmentStyle(1)}
        >
          {/* Title */}
          <h2
            className="font-semibold text-[18px] leading-[26px] text-[#030407] m-0 p-0"
            style={{ height: 26 }}
          >
            Remove pass?
          </h2>
          {/* Description */}
          <div
            className="flex flex-col gap-[24px] text-[16px] leading-[24px] text-[#667085] m-0 p-0"
            style={{ height: 96 }}
          >
            <p className="m-0 p-0">
              {REMOVE_PASS_DESCRIPTION_1}
            </p>
            <p className="m-0 p-0">
              {REMOVE_PASS_DESCRIPTION_2}
            </p>
          </div>
        </div>

        {/* ========== SECOND COMPARTMENT ========== */}
        <div
          className={getCompartmentClass(2)}
          style={getCompartmentStyle(2)}
        >
          {/* Keep pass */}
          <button
            onClick={onKeep}
            className="flex items-center justify-center font-bold text-[16px] leading-[20px] text-white"
            style={{
              width: 110,
              height: 48,
              borderRadius: 8,
              background: '#030407',
              fontFamily: 'var(--font-button, inherit)',
              letterSpacing: 0,
            }}
          >
            Keep pass
          </button>

          {/* Remove pass clickable text */}
          <div
            onClick={onRemove}
            role="button"
            tabIndex={0}
            className="flex items-center cursor-pointer select-none whitespace-nowrap"
            style={{
              width: 91,
              height: 20,
              borderRadius: 0,
              background: 'transparent',
              color: 'var(--Core-Colours-Text-Neutral_grey-TextNeutral_grey_base, #667085)',
              fontFamily: 'var(--font-button, inherit)',
              fontWeight: 700,
              fontStyle: 'bold',
              fontSize: 16,
              lineHeight: '20px',
              letterSpacing: 0,
              padding: 0,
              opacity: 1,
              boxSizing: 'border-box',
              outline: 'none',
              border: 'none',
              alignItems: 'center',
              userSelect: 'none',
              textAlign: 'left',
            }}
            onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') onRemove(); }}
          >
            Remove pass
          </div>
        </div>
      </div>
    </div>
  );
};

// Responsive compartment styles for RemovePassModal (mobile)
const getCompartmentStyle = (compartment: number): React.CSSProperties => {
  if (typeof window !== 'undefined' && window.innerWidth <= 767) {
    if (compartment === 1) {
      return {
        width: 215,
        height: 224,
        gap: 6,
      };
    }
    if (compartment === 2) {
      return {
        width: 215,
        height: 92,
        gap: 24,
      };
    }
  }
  // Default desktop styles
  if (compartment === 1) {
    return { width: 478, height: 128, gap: 6 };
  }
  if (compartment === 2) {
    return { width: 478, height: 48, gap: 24 };
  }
  return {};
};
// Responsive styles for RemovePassModal (mobile)
const getModalStyle = (): React.CSSProperties => {
  if (typeof window !== 'undefined' && window.innerWidth <= 767) {
    return {
      width: 295,
      height: 416,
      maxWidth: 558,
      paddingTop: 40,
      paddingRight: 40,
      paddingLeft: 40,
      paddingBottom: 24,
      gap: 24,
      borderRadius: 0,
      background: 'var(--Core-Colours-Backgrounds-Neutral_grey-BgNeutral_grey_white, #FFFFFF)',
      boxShadow: '0px 10px 30px 0px #0C111D24',
    };
  }
  return {
    width: 558,
    height: 276,
    maxWidth: 558,
    padding: 40,
    gap: 20,
    borderRadius: '16px',
    background: 'var(--Core-Colours-Backgrounds-Neutral_grey-BgNeutral_grey_white, #FFFFFF)',
    boxShadow: '0px 10px 30px 0px #0C111D24',
  };
};

// Responsive flex direction for second compartment
const getCompartmentClass = (compartment: number): string => {
  if (compartment === 2 && typeof window !== 'undefined' && window.innerWidth <= 767) {
    return 'flex flex-col items-start gap-[22px]';
  }
  if (compartment === 2) {
    return 'flex items-center';
  }
  return 'flex flex-col';
};

export default RemovePassModal;
